/* eslint-disable no-param-reassign, prefer-template */
import Logger from 'es6/services/logger';
import { ResultTypes } from 'es6/models/quiz-result';
import SequencerBase from 'es6/controllers/apps/sequencer-base';

const MAX_QUIZZES = 20;

export default class DiagSequencer extends SequencerBase {
  constructor(bands, quizTimer) {
    // Collect up studyItems from accross all the bands.
    const studyItems = _.chain(bands)
      .map((band, index) => {
        const items = band.diagStudy ? band.diagStudy.content : [];

        // Index the items with what band they came from.
        _.each(items, item => {
          item.band = index;
        });

        return items;
      })
      .flatten(true)
      .compact()
      .value();

    super(studyItems, true, quizTimer);

    this._bandsCount = bands.length;
    this._studyItemsByBand = _.groupBy(this._studyItems, 'band');
    this._quizzesSeen = 0;

    // Adjustments are scaled based on the number of bands.
    this._adjustmentBase = bands.length / 10;
    this._currentPosition = this._adjustmentBase * 4;
    this._adjustmentFactor = this._adjustmentBase * 1.5;
    this._minimumAdjustment = this._adjustmentBase * 0.5;

    Logger.info('BandCount: ' + this._bandsCount);
    Logger.info('this._currentPosition: ' + this._currentPosition);
    Logger.info('this._adjustmentFactor: ' + this._adjustmentFactor);
    Logger.info('this._minimumAdjustment: ' + this._minimumAdjustment);
  }

  // Computed property that maps the continuous position to a discrete band.
  get _currentBand() {
    // -2 is because -1 for zero indexing and an extra -1 as the last band doesn't include items.
    return Math.floor(Math.max(Math.min(this._bandsCount - 2, this._currentPosition), 0));
  }

  showNextQuiz() {
    Logger.info('Adjustment Factor: ' + this._adjustmentFactor);
    Logger.info('Current Position: ' + this._currentPosition);

    const nextStudyItem = this._getStudyItemFromBand(this._currentBand);

    if (
        // Done sufficient quizzes that we should have settled on correct band.
        this._quizzesSeen >= MAX_QUIZZES ||
        // Test out by going 1 adjustment below/above the available bands.
        this._currentPosition <= 0 - this._adjustmentBase ||
        this._currentPosition >= this._bandsCount - 1 + this._adjustmentBase ||
        // Exhausted all the items in the current band, so place in that band.
        !nextStudyItem
      ) {
      this.sequenceFinished();
      return;
    }

    Logger.info(nextStudyItem.content.item);

    this._showNextQuizForItem(nextStudyItem);
  }

  _getStudyItemFromBand(band) {
    const quizzes = this._itemsWithQuizzesRemaining(this._studyItemsByBand[band]);

    if (quizzes.length === 0) {
      Logger.warn('IntraSessionSequencer', 'All items in band ' + band + ' have already been seen');
      return null;
    }

    return _.sample(quizzes);
  }

  _expectedRemainingQuizCount() {
    // Expect the user will go through the max number of quizzes.
    return MAX_QUIZZES - this._quizzesSeen;
  }

  quizFinished(quiz) {
    this._quizzesSeen++;

    const studyItem = this._studyItemFromQuiz(quiz);
    studyItem.sequencing.presentedQuizzes.push(quiz);

    // TODO account for relative quiz difficulty
    // TODO take Yields into account
    if (quiz.result.isCorrect()) {
      this._currentPosition += this._adjustmentFactor;
    } else if (quiz.result.leQuizResult === ResultTypes.ALMOST_CORRECT) {
      // Do nothing, probably about the right level.
    } else if (_.contains([ResultTypes.YIELD_ON_RECALL, ResultTypes.YIELD_ON_QUIZ], quiz.result.leQuizResult)) {
      // Not as bad as getting getting it wrong.
      this._currentPosition -= this._adjustmentFactor * 0.7;
    } else {
      this._currentPosition -= this._adjustmentFactor;
    }
    this._reduceAdjustment();

    this._incrementProgress();
    this.showNextQuiz();
  }

  _reduceAdjustment() {
    // Reduce the size of the adjustment factor above the minumum by 20%
    this._adjustmentFactor = (this._adjustmentFactor - this._minimumAdjustment) * 0.8 + this._minimumAdjustment;
  }

  sequenceFinished() {
    this.activeQuiz = null;
    this._sessionFinishedCallback({
      presentationResults: this._collectPresentationResults(),
      placementBand:       Math.floor(Math.max(Math.min(this._bandsCount - 1, this._currentPosition), 0)),
    });
  }
}
